/*
* Order view for authenticated users
*/

import * as _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { Button, ButtonToolbar, Pagination, Row, Well } from 'react-bootstrap';
import Dialog from 'react-bootstrap-dialog';
import { FormattedNumber } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { push } from 'react-router-redux';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';

import * as MutateActions from '../actions/mutate';
import * as AppComp from '../common/app-component-base';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import CopyAddressToClipboard from '../common/copyaddresstoclipboard';
import { AddressSection, EntityDetailSection, SectionField } from '../common/entitydetail/entitydetailsection';
import EntityNotes from '../common/entitydetail/entitynotes';
import { Formatters } from '../common/formatters';
import { FormCheckbox } from '../common/formcheckbox';
import { EmailLinkComponent, NavLinkComponent, NavigationLinks } from '../common/navigation';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import AppConfiguration from '../config/configuration';
import * as DetailFormCommon from '../inventory/detail-form/common';
import * as ReduxQueries from '../queries';
import { OrderPricing } from './orderpricing';

export interface IOrderDetailProps extends AppComp.IAppComponentProps {
    orderDetail?: any;
    orderChartDetail?: any;
    orders?: any;
    navigateTo?: any;
    newOrder?: any;
    orderId?: any;
    association?: any;
    assocId?: any;
    orderObjId?: any;
    updateReminderBidsSendReminderEmail?: any;
}

export interface IOrderDetailState extends AppComp.IAppComponentState {
    formChanges?: any;
}

class Order extends AppComp.AppComponent<IOrderDetailProps, IOrderDetailState> {
    public MomentDateFormat: string = AppConfiguration.getMomentShortDateFormat();
    public MomentDateTimeFormat: string = AppConfiguration.getMomentDateTimeFormat();

    public dialogRef: any;

    public constructor(props: IOrderDetailProps) {
        super(props, 'app.order-detail.');

        this.state = {
            formChanges: {},
        };
    }

    public componentDidMount() {
        const enforce = this.enforceAdminOrEmployeeOnly();

        if (enforce) {
            this.props.navigateTo(NavigationLinks.userDashboard());
        }
    }

    public formattedDate(date: any) {
        // Returns a formatted date, or empty

        return date ? moment(new Date(date)).format(this.MomentDateFormat) : '';
    }

    public extendOrder(order: any, orderReserveStudyChart?: any) {
        if (!order) { return {}; }

        let record = _.cloneDeep(order);

        record.associationAddress = Formatters.composeLocationFullAddress(_.get(order, 'association.location'));

        record.contactUser = Formatters.composeClientContactFormal(record.contact);

        record.managerUser = Formatters.composeUsernameLabel(record.user, true);
        record.coordinatorUser = Formatters.composeUsernameLabel(record.siteInspector, true);

        record.wonDateFormatted = this.formattedDate(_.get(record, 'wonDate'));
        record.startDateFormatted = this.formattedDate(_.get(record, 'startDate'));
        record.siDateFormatted = this.formattedDate(_.get(record, 'siDueDate'));
        record.dueDateFormatted = this.formattedDate(_.get(record, 'dueDate'));
        record.completedDateFormatted = this.formattedDate(_.get(record, 'completionDate'));
        record.bidReceivedDateFormatted = this.formattedDate(_.get(record, 'bidDate'));
        record.bidSentDateFormatted = this.formattedDate(_.get(record, 'bidSentDate'));

        // fye
        let preparedForFYE;
        const orderChartBeginYear = _.get(orderReserveStudyChart, 'beginYear');
        const assocFiscalYearEnd = _.get(record, 'association.details.fiscalYearEnd');

        if (orderChartBeginYear && assocFiscalYearEnd) {
            const fyeDateStr = `${orderChartBeginYear}/${assocFiscalYearEnd}`;
            const dateEndAssocFYE = new Date(fyeDateStr);
            const reportPeriodEndDateMoment = moment(dateEndAssocFYE);

            preparedForFYE = moment(reportPeriodEndDateMoment).toDate();
        }

        record.preparedForFYEDateFormatted = this.formattedDate(preparedForFYE);
        record.assocFYE = assocFiscalYearEnd;
        record.followUpDateFormatted = this.formattedDate(_.get(record, 'followUpDate'));
        record.reminderBidDateFormatted = this.formattedDate(_.get(record, 'reminderBidDate'));
        record.firstReminderSentDateFormatted = this.formattedDate(_.get(record, 'firstReminderSentDate'));
        record.secondReminderSentDateFormatted = this.formattedDate(_.get(record, 'secondReminderSentDate'));
        record.isPreliminaryStudy = _.get(order, 'isPreliminaryStudy', false);

        return record;
    }

    public attachDialogRef(el: any) {
        this.dialogRef = el;
    }

    // Button handlers
    public handleEditButton(e: any) {
        const orderId = this.props.match.params.orderId;

        this.props.navigateTo(NavigationLinks.orderEdit(orderId));
    }

    public handleCreateOrderForAssocButton(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');
        const { orderDetail, orderChartDetail } = this.props;

        const order = this.extendOrder(orderDetail, orderChartDetail);

        const associationId = _.get(order, 'associationId', 0);

        const changeData = _.merge(
            {},
            { association: { associationId: associationId } },
            { client: { clientId: -1 } },
        );

        this.props.newOrder(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                // FORCE RELOAD
                const newOrderId = _.get(response, 'body.orderId');

                this.props.navigateTo(NavigationLinks.orderEdit(newOrderId));
            } else {
                // Error during creation...
            }
        });
    }

    public handleViewAssociationButton(e: any) {
        const { orderDetail, orderChartDetail } = this.props;

        const order = this.extendOrder(orderDetail, orderChartDetail);

        const associationId = _.get(order, 'associationId', 0);

        this.props.navigateTo(NavigationLinks.associationDetail(associationId));
    }

    public handleResearchBidsButton(e: any) {
        this.props.navigateTo(NavigationLinks.researchBids());
    }

    public handleOrderSendReminderEmailButton(e: any) {
        const assocId = _.get(this.props.association, 'associationId');
        const orderId = this.props.match.params.orderId;
        const orderObjId = _.get(this.props.orderDetail, '_id');
        const token = _.get(this.props, 'authsession.access_token', '');

        const sendData = _.merge({}, { _id: orderObjId, associationId: assocId, orderId: orderId, reminderDate: '' });
        const ordersArray = _.stubArray();
        ordersArray.push(sendData);

        const changeData = _.merge(
            {},
            { orders: ordersArray },
        );

        this.props.updateReminderBidsSendReminderEmail(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                // FORCE RELOAD
                this.setState({ formChanges: {} });
                this.props.navigateTo(NavigationLinks.orderDetail(orderId));
            } else {
                // Error during creation...
            }
        });
    }

    public handleOrderBidLetterPdfButton(e: any) {
        const orderId = this.props.match.params.orderId;

        this.props.navigateTo(NavigationLinks.orderBidLetterPdf(orderId));
    }

    public handleOrderBidEnvelopePdfButton(e: any) {
        const orderId = this.props.match.params.orderId;
        this.props.navigateTo(NavigationLinks.orderBidEnvelopePdf(orderId));
    }

    // Renderers
    public renderHeaderButtonToolbar = (orderId: any, allOrders: any) => {
        let buttonProps = {
            bsSize: 'sm'
        };
        const orderedByBidDate = _.orderBy(allOrders, 'bidDate', 'asc');

        const curOrderIndex = _.findIndex(orderedByBidDate, (c) => Number(_.get(c, 'orderId', 0)) === Number(orderId));
        const firstOrderId = _.get(orderedByBidDate, '[0].orderId', orderId);
        const lastOrderId = _.get(orderedByBidDate, `[${_.size(orderedByBidDate) - 1}].orderId`, orderId);
        const prevOrderId = (curOrderIndex !== 0) ? _.get(orderedByBidDate, `[${curOrderIndex - 1}].orderId`, orderId) : firstOrderId;
        const nextOrderId = (curOrderIndex !== _.size(orderedByBidDate) - 1) ? _.get(orderedByBidDate, `[${curOrderIndex + 1}].orderId`, orderId) : lastOrderId;
        const activeLabel = `${curOrderIndex + 1} ${this.appTextCommon('buttons.of')} ${Formatters.plural(this.appText('order'), _.size(orderedByBidDate), true)}`;

        const pagination = (
            <Pagination bsSize="sm">
                <li>
                    <Link to={NavigationLinks.orderDetail(firstOrderId)}>
                        <i className="glyphicon glyphicon-step-backward" />
                    </Link>
                </li>
                <li>
                    <Link to={NavigationLinks.orderDetail(prevOrderId)}>
                        <i className="glyphicon glyphicon-chevron-left" />
                    </Link>
                </li>
                <Pagination.Item className="active">{activeLabel}</Pagination.Item>
                <li>
                    <Link to={NavigationLinks.orderDetail(nextOrderId)}>
                        <i className="glyphicon glyphicon-chevron-right" />
                    </Link>
                </li>
                <li>
                    <Link to={NavigationLinks.orderDetail(lastOrderId)}>
                        <i className="glyphicon glyphicon-step-forward" />
                    </Link>
                </li>
            </Pagination>
        );

        return (
            <div>
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={9}>
                            <ButtonToolbar>
                                <Button {...buttonProps} bsStyle="info" onClick={e => { this.handleEditButton(e); }}>
                                    <i className="glyphicon glyphicon-edit" /> {this.appTextCommon('buttons.edit')}
                                </Button>

                                <Button {...buttonProps} bsStyle="info" onClick={e => { this.handleCreateOrderForAssocButton(e); }}>
                                    <i className="glyphicon glyphicon-plus" /> {this.appText('new-order')}
                                </Button>

                                <Button {...buttonProps} bsStyle="info" onClick={e => { this.handleViewAssociationButton(e); }}>
                                    <i className="glyphicon glyphicon-eye-open" /> {this.appTextCommon('buttons.view-association')}
                                </Button>
                            </ButtonToolbar>
                        </Col>

                        <div>
                            <div className="pagination-container">{pagination}</div>
                        </div>
                    </Row>
                </Well>
            </div>
        );
    }

    public renderLinksButtonToolbar() {
        return (
            <div className="order-edit-links text-center">
                <ButtonToolbar className="center-block">
                    <Button bsSize="xs" bsStyle="info" onClick={e => { this.handleResearchBidsButton(e); }}>
                        <i className="fa fa-search" /> {this.appText('research-bids')}
                    </Button>

                    <Button bsSize="xs" bsStyle="info" onClick={e => { this.handleOrderSendReminderEmailButton(e); }}>
                        <i className="fa fa-envelope" /> {this.appText('send-reminder-email')}
                    </Button>

                    <Button bsSize="xs" bsStyle="info" onClick={e => { this.handleOrderBidLetterPdfButton(e); }}>
                        <i className="fa fa-file" /> {this.appText('view-bid-letter')}
                    </Button>

                    <Button bsSize="xs" bsStyle="info" onClick={e => { this.handleOrderBidEnvelopePdfButton(e); }}>
                        <i className="fa fa-envelope" /> {this.appText('print-envelope')}
                    </Button>
                </ButtonToolbar>
            </div>
        );
    }

    // Disables 4/20/20 <CSRA-28>https://complex-solutions.atlassian.net/browse/CSRA-28</CSRA-28>
    /*   public renderOrderNotes(formData: any) {
          return (
              <DetailFormCommon.OrderNotes
                  name="orderNotesDelete"
                  orderNotes={formData.orderNotes}
                  onClickDelete={(e, o) => { console.log('onClickDelete'); }}
                  onClickSave={(e) => { console.log('onClickSave'); }}
                  deleteButtonVisible={false}
                  saveButtonVisible={false}
              />
          );
      } */

    public renderOrderFollowUpNotes(formData: any) {
        return (
            <DetailFormCommon.OrderFollowUpNotes
                name="followUpNotesDelete"
                orderFollowUpNotes={formData.followUpNotes}
                onClickDelete={(e, o) => { /* console.log('onClickDelete'); */ }}
                onClickSave={(e) => { /* console.log('onClickSave'); */ }}
                deleteButtonVisible={false}
                saveButtonVisible={false}
            />
        );
    }

    public getFormData(orderIn: any, orderId: any) {
        const order = _.merge(orderIn, this.state.formChanges);

        let result = {
            association: _.get(order, 'association'),
            associationId: Formatters.getInt(order, 'associationId'),

            company: {
                companyId: Formatters.getInt(order, 'company.companyId'),
            },

            client: _.get(order, 'client'),
            clientId: Formatters.getInt(order, 'clientId'),

            orderId: orderId,
            orderObjId: _.get(order, '_id'),

            orderDashNumber: _.get(order, 'orderId') + '-' + Formatters.getOrderDashLetter(order),
            orderDashNumberNum: Formatters.getInt(order, 'dash'),
            orderDashNumberLet: Formatters.getOrderDashLetter(order),

            user: _.get(order, 'user'),
            siteInspector: _.get(order, 'siteInspector'),

            bidDate: _.get(order, 'bidDate'),
            bidSentDate: _.get(order, 'bidSentDate'),
            completionDate: _.get(order, 'completionDate'),
            dueDate: _.get(order, 'dueDate'),
            followUpDate: _.get(order, 'followUpDate'),
            firstReminderSendDate: _.get(order, 'firstReminderSentDate'),
            secondReminderSendDate: _.get(order, 'secondReminderSentDate'),
            preparedForFYEDate: _.get(order, 'preparedForFYEDate'),
            preparedForFYEDateFormatted: _.get(order, 'preparedForFYEDateFormatted'),
            reminderBidDate: _.get(order, 'reminderBidDate'),
            siDueDate: _.get(order, 'siDueDate'),
            startDate: _.get(order, 'startDate'),
            wonDate: _.get(order, 'wonDate'),

            feedback: _.get(order, 'feedback'),
            sampleSent: _.get(order, 'sampleSent'),
            unsolicitedBid: _.get(order, 'unsolicitedBid'),
            contractReceived: _.get(order, 'contractReceived'),
            enteredInQuickbooks: _.get(order, 'enteredInQuickbooks'),
            invoiceSentInQuickbooks: _.get(order, 'invoiceSentInQuickbooks'),
            inspectorPaid: _.get(order, 'inspectorPaid'),
            worksheetReceived: _.get(order, 'worksheetReceived'),

            acceptedPrice: Formatters.getFloat(order, 'acceptedPrice'),
            status: _.get(order, 'status'),
            serviceLevel: _.get(order, 'serviceLevel'),
            serviceLevelId: Formatters.getInt(order, 'serviceLevelId'),

            contact: _.get(order, 'contact'),
            contactId: Formatters.getInt(order, 'contactId'),

            pricingMatrix: _.get(order, 'pricingMatrix'),
            orderServicesPricingMatrix: _.get(order, 'orderServicesPricingMatrix', {}), // child doc with rushPricing, etc

            // Order Pricing: 3 year plan future years
            pricingMatrix3YearPlan: [
                { futureYearIndex: 1, year: 0, planYearDate: new Date(), levelDue: '', disabled: true },
                { futureYearIndex: 2, year: 0, planYearDate: new Date(), levelDue: '', disabled: true },
                { futureYearIndex: 3, year: 0, planYearDate: new Date(), levelDue: '', disabled: true },
            ],

            orderNotesAdd: Formatters.getStr(order, 'orderNotesAdd'),
            orderNotesDelete: _.get(order, 'orderNotesDelete', []),
            orderNotes: _.get(order, 'notes', []),

            followUpNotesAdd: Formatters.getStr(order, 'followUpNotesAdd'),
            followUpNotesDelete: _.get(order, 'followUpNotesDelete', []),
            followUpNotes: _.get(order, 'followUpNotes', []),

            setDueDateWeeks: _.get(order, 'setDueDateWeeks', 1),
            newDueDate: new Date(),

            isPreliminaryStudy: _.get(order, 'isPreliminaryStudy'),
        };

        // 3 Year plan future year
        const prepFYEDate = _.get(result, 'preparedForFYEDate') ? new Date(result.preparedForFYEDate) : null;
        const prepFYEDateYear = prepFYEDate ? moment(prepFYEDate).year() : null;

        if (prepFYEDateYear) {
            result.pricingMatrix3YearPlan[0].year = prepFYEDateYear + 0;
            result.pricingMatrix3YearPlan[0].planYearDate = new Date(result.pricingMatrix3YearPlan[0].year, 0, 1, 1, 1, 1, 1);
            result.pricingMatrix3YearPlan[0].levelDue = this.appText('pricing-3-year-plan-level-due.site-visit');
            result.pricingMatrix3YearPlan[0].disabled = false;

            result.pricingMatrix3YearPlan[1].year = prepFYEDateYear + 1;
            result.pricingMatrix3YearPlan[1].planYearDate = new Date(result.pricingMatrix3YearPlan[1].year, 0, 1, 1, 1, 1, 1);
            result.pricingMatrix3YearPlan[1].levelDue = this.appText('pricing-3-year-plan-level-due.no-site-visit');
            result.pricingMatrix3YearPlan[1].disabled = false;

            result.pricingMatrix3YearPlan[2].year = prepFYEDateYear + 2;
            result.pricingMatrix3YearPlan[2].planYearDate = new Date(result.pricingMatrix3YearPlan[2].year, 0, 1, 1, 1, 1, 1);
            result.pricingMatrix3YearPlan[2].levelDue = this.appText('pricing-3-year-plan-level-due.no-site-visit');
            result.pricingMatrix3YearPlan[2].disabled = false;
        }

        // Order notes
        if (_.size(result.orderNotes)) {
            result.orderNotes = _.filter(result.orderNotes, (o) => Formatters.getStr(o, 'notes') !== '');
            if (_.size(result.orderNotes)) {
                result.orderNotes = _.orderBy(result.orderNotes, ['created'], ['desc']);
            }
        }

        if (_.size(result.orderNotesDelete)) {
            result.orderNotes = _.omitBy(result.orderNotes, (o) => _.includes(result.orderNotesDelete, o.objectId));
        }

        // Follow up notes
        if (_.size(result.followUpNotes)) {
            result.followUpNotes = _.filter(result.followUpNotes, (o) => Formatters.getStr(o, 'notes') !== '');
            if (_.size(result.followUpNotes)) {
                result.followUpNotes = _.orderBy(result.followUpNotes, ['created'], ['desc']);
            }
        }

        if (_.size(result.followUpNotesDelete)) {
            result.followUpNotes = _.omitBy(result.followUpNotes, (o) => _.includes(result.followUpNotesDelete, o.objectId));
        }

        const res = _.merge(result, this.state.formChanges);

        return res;
    }

    public render() {
        const { orderDetail, orderChartDetail, orderId, orders, orderObjId, assocId } = this.props;

        let sections = {
            enabled: false,
            title: '',
            activeContactLeftAddress: _.stubArray(),
            activeContactLeftDetails: _.stubArray(),
            activeContactRightAssociationClient: _.stubArray(),
            activeContactRightAssociationContact: _.stubArray(),
            activeContactRightOrderClient: _.stubArray(),
            activeContactRightOrderContact: _.stubArray(),
            orderDetailsLeftDates: _.stubArray(),
            orderDetailsLeftInfo: _.stubArray(),
            orderDetailsLeftInfoAssigned: _.stubArray(),
            orderDetailsLeftInfoNotes: _.stubArray(),
            orderDetailsLeftPricing: _.stubArray(),
            orderDetailsLeftPricing3YearPlan: _.stubArray(),
            orderDetailsLeftServiceInfo: _.stubArray(),
            orderDetailsRight: _.stubArray(),
        };

        // const formData = this.getFormData(orderDetail, orderId);
        let formData = this.getFormData(orderDetail, orderId);

        // Determine if 3 year plan are is visible
        let fdOrderSelectedService = Formatters.getInt(formData, 'serviceLevelId');
        let fdOrderIs3YearPlan = false;
        switch (fdOrderSelectedService) {
            case OrderPricing.ServiceLevelConfig.economy.level4.serviceLevelId:
            case OrderPricing.ServiceLevelConfig.economy.level4_2.serviceLevelId:
            case OrderPricing.ServiceLevelConfig.economy.level4_3.serviceLevelId:
            case OrderPricing.ServiceLevelConfig.standard.level4.serviceLevelId:
            case OrderPricing.ServiceLevelConfig.standard.level4_2.serviceLevelId:
            case OrderPricing.ServiceLevelConfig.standard.level4_3.serviceLevelId:
            case OrderPricing.ServiceLevelConfig.rush.level4.serviceLevelId:
            case OrderPricing.ServiceLevelConfig.rush.level4_2.serviceLevelId:
            case OrderPricing.ServiceLevelConfig.rush.level4_3.serviceLevelId:
                fdOrderIs3YearPlan = true;
                break;
            default:
                fdOrderIs3YearPlan = false;
                break;
        }

        // 20200127: Always show "3 Year Plan" pricing section regardless of selected serviceLevel
        fdOrderIs3YearPlan = true;

        const orderAssoc = this.props.association;

        if (orderDetail) {
            const order = this.extendOrder(orderDetail, orderChartDetail);
            formData = this.getFormData(orderDetail, orderId);

            sections.enabled = true;

            const orderDashNumberLetter = _.get(order, 'orderId') + '-' + Formatters.getOrderDashLetter(order);
            const orderDashNumber = _.get(order, 'orderId') + '-' + Formatters.getInt(order, 'dash');

            const orderDashNumberText = `${orderDashNumber} / ${orderDashNumberLetter}`;

            sections.title = this.appTextPageTitleAppendString(orderDashNumber);

            const associationId = _.get(order, 'associationId', 0);
            const associationIdLink = (<NavLinkComponent caption={`${associationId}`} linkDetails={{ to: NavigationLinks.associationDetail(associationId), title: this.appText('view-association') }} />);

            const associationNameLink = (
                <div>
                    <NavLinkComponent caption={_.get(order, 'association.name')} linkDetails={{ to: NavigationLinks.associationDetail(associationId), title: this.appText('view-association') }} />
                    <div>
                        <CopyAddressToClipboard locationObj={_.get(order, 'association.location')} />
                    </div>
                </div>
            );

            sections.activeContactLeftAddress = AddressSection.CreateAddressSectionFields(_.get(order, 'association.location'), (a, b, c) => this.appText(a, b, c));
            sections.activeContactLeftAddress.unshift(SectionField.CreateSingle(this.appText('association-name'), associationNameLink));

            sections.activeContactLeftDetails.push(SectionField.CreateSingle(this.appText('association-id'), associationIdLink));
            sections.activeContactLeftDetails.push(SectionField.CreateSingle(this.appText('association-type'), _.get(order, 'association.associationType.name')));

            // Order client/contact info
            const orderClient = _.get(order, 'client');
            const orderClientId = Formatters.getInt(orderClient, 'clientId');
            const orderClientNameLink = (<NavLinkComponent caption={Formatters.getStr(orderClient, 'name')} linkDetails={{ to: NavigationLinks.clientDetail(orderClientId), title: this.appText('view-client') }} />);
            sections.activeContactRightOrderClient.push(SectionField.CreateSingle(this.appText('client-id'), orderClientId));
            sections.activeContactRightOrderClient.push(SectionField.CreateSingle(this.appText('client-name'), orderClientNameLink));
            sections.activeContactRightOrderClient.push(SectionField.CreateSingle(this.appText('client-type'), Formatters.getStr(orderClient, 'clientType.name')));
            sections.activeContactRightOrderClient.push(SectionField.CreateSingle(this.appText('client-phone'), Formatters.formatPhoneNumber(Formatters.getStr(orderClient, 'phone'))));

            const clientContactEmailLink = (<EmailLinkComponent email={Formatters.getStr(order, 'contact.email')} caption={`${this.appTextCommon('contacts.send-email-to')} ${Formatters.getStr(order, 'contact.email')}`} />);
            sections.activeContactRightOrderContact.push(SectionField.CreateSingle(this.appText('client-contact-id'), _.get(order, 'contact.contactId')));
            sections.activeContactRightOrderContact.push(SectionField.CreateSingle(this.appText('client-manager'), _.get(order, 'contactUser')));
            if (_.get(order, 'contact.phone')) { sections.activeContactRightOrderContact.push(SectionField.CreateSingle(this.appText('client-contact-phone'), Formatters.formatPhoneNumber(Formatters.getStr(order, 'contact.phone')))); }
            sections.activeContactRightOrderContact.push(SectionField.CreateSingle(this.appText('client-contact-email'), clientContactEmailLink));

            // Association client/contact info
            const assocClient = _.get(orderAssoc, 'client');
            const assocClientId = Formatters.getInt(assocClient, 'clientId');
            const assocClientNameLink = (<NavLinkComponent caption={Formatters.getStr(assocClient, 'name')} linkDetails={{ to: NavigationLinks.clientDetail(assocClientId), title: this.appText('view-client') }} />);
            sections.activeContactRightAssociationClient.push(SectionField.CreateSingle(this.appText('client-id'), assocClientId));
            sections.activeContactRightAssociationClient.push(SectionField.CreateSingle(this.appText('client-name'), assocClientNameLink));
            sections.activeContactRightAssociationClient.push(SectionField.CreateSingle(this.appText('client-type'), Formatters.getStr(assocClient, 'clientType.name')));
            sections.activeContactRightAssociationClient.push(SectionField.CreateSingle(this.appText('client-phone'), Formatters.formatPhoneNumber(Formatters.getStr(assocClient, 'phone'))));

            const assocClientContactEmailLink = (<EmailLinkComponent email={Formatters.getStr(orderAssoc, 'contact.email')} caption={`${this.appTextCommon('contacts.send-email-to')} ${Formatters.getStr(orderAssoc, 'contact.email')}`} />);
            sections.activeContactRightAssociationContact.push(SectionField.CreateSingle(this.appText('client-contact-id'), _.get(orderAssoc, 'contact.contactId')));
            sections.activeContactRightAssociationContact.push(SectionField.CreateSingle(this.appText('client-manager'), Formatters.composeClientContactFormal(_.get(orderAssoc, 'contact'))));
            if (_.get(order, 'association.contact.phone')) {
                sections.activeContactRightAssociationContact.push(SectionField.CreateSingle(this.appText('client-contact-phone'), Formatters.formatPhoneNumber(Formatters.getStr(orderAssoc, 'contact.phone'))));
            }
            sections.activeContactRightAssociationContact.push(SectionField.CreateSingle(this.appText('client-contact-email'), assocClientContactEmailLink));

            sections.orderDetailsLeftInfo.push(SectionField.CreateSingle(this.appText('order-number'), orderDashNumberText));

            sections.orderDetailsLeftInfoAssigned.push(SectionField.CreateSingle(this.appText('order-manager-assigned'), _.get(order, 'managerUser')));
            sections.orderDetailsLeftInfoAssigned.push(SectionField.CreateSingle(this.appText('order-coordinator-assigned'), _.get(order, 'coordinatorUser')));

            const orderNotes = (<EntityNotes notes={_.orderBy(_.get(order, 'notes'), ['created'], ['desc'])} />);
            sections.orderDetailsLeftInfoNotes.push(SectionField.CreateSingle(this.appText('order-notes'), orderNotes));

            sections.orderDetailsLeftDates = [(
                <Row key="order-dates-header-1">
                    <Col xs={3} className="entity-detail-label"><strong>{this.appText('order-bid-received-date')}</strong></Col>
                    <Col xs={3} className="entity-detail-label"><strong>{this.appText('order-won-date')}</strong></Col>
                    <Col xs={2} className="entity-detail-label"><strong>{this.appText('order-start-date')}</strong></Col>
                    <Col xs={2} className="entity-detail-label"><strong>{this.appText('order-si-date')}</strong></Col>
                    <Col xs={2} className="entity-detail-label"><strong>{this.appText('order-due-date')}</strong></Col>
                </Row>
            ),
            (
                <Row key="order-dates-values-1">
                    <Col xs={3} className="entity-detail-values">{Formatters.getStr(order, 'bidReceivedDateFormatted')}</Col>
                    <Col xs={3} className="entity-detail-values">{Formatters.getStr(order, 'wonDateFormatted')}</Col>
                    <Col xs={2} className="entity-detail-values">{Formatters.getStr(order, 'startDateFormatted')}</Col>
                    <Col xs={2} className="entity-detail-values">{Formatters.getStr(order, 'siDateFormatted')}</Col>
                    <Col xs={2} className="entity-detail-values">{Formatters.getStr(order, 'dueDateFormatted')}</Col>
                </Row>
            ),
            (
                <Row key="order-dates-spacer-1"><Col xs={12}><div>&nbsp;</div></Col></Row>
            ),
            (
                <Row key="order-dates-header-2">
                    <Col xs={3} className="entity-detail-label"><strong>{this.appText('order-bid-sent-date')}</strong></Col>
                    <Col xs={3} className="entity-detail-label"><strong>{this.appText('order-completed-date')}</strong></Col>
                    <Col xs={3} className="entity-detail-label"><strong>{this.appText('prepared-for-fye')}</strong></Col>
                    <Col xs={2} className="entity-detail-label" />
                    <Col xs={2} className="entity-detail-label" />
                </Row>
            ),
            (
                <Row key="order-dates-values-2">
                    <Col xs={3} className="entity-detail-values">{Formatters.getStr(order, 'bidSentDateFormatted')}</Col>
                    <Col xs={3} className="entity-detail-values">{Formatters.getStr(order, 'completedDateFormatted')}</Col>
                    <Col xs={3} className="entity-detail-values">{Formatters.getStr(order, 'preparedForFYEDateFormatted')}</Col>
                    <Col xs={2} className="entity-detail-values" />
                    <Col xs={2} className="entity-detail-values" />
                </Row>
            )
            ];

            sections.orderDetailsLeftServiceInfo = [(
                <Row key="order-service-info-header">
                    <Col xs={3} className="entity-detail-label"><strong>{this.appText('order-selected-service')}</strong></Col>
                    <Col xs={3} className="entity-detail-label"><strong>{this.appText('order-status')}</strong></Col>
                    <Col xs={3} className="entity-detail-label"><strong>{this.appText('order-accepted-price')}</strong></Col>
                </Row>
            ),
            (
                <Row key="order-service-info-values">
                    <Col xs={3} className="entity-detail-values">{Formatters.getStr(order, 'serviceLevel.name')}</Col>
                    <Col xs={3} className="entity-detail-values">{Formatters.getStr(order, 'status.name')}</Col>
                    <Col xs={3} className="entity-detail-values"><FormattedNumber value={Formatters.getFloat(order, 'acceptedPrice')} style="currency" currency="USD" /></Col>
                </Row>
            ),
            ];

            sections.orderDetailsLeftPricing = _.concat(
                [(
                    <Row key="pricing-matrix-header">
                        <Col xs={3} className="entity-detail-label">
                            <div />
                            {/* <Button type="button" bsStyle="primary" bsSize="xsmall" onClick={e => { this.handleSetPricesButton(e); }}>{this.appText('set-prices')}</Button> */}
                        </Col>
                        <Col xs={3} className="entity-detail-label"><strong>{this.appText('pricing-economy')}</strong></Col>
                        <Col xs={3} className="entity-detail-label"><strong>{this.appText('pricing-standard')}</strong></Col>
                        <Col xs={3} className="entity-detail-label"><strong>{this.appText('pricing-rush')}</strong></Col>
                    </Row>
                )],
                order.pricingMatrix.map((pricingLevelLeft, i) => {
                    const isPricingLevel4 = (Formatters.getStr(pricingLevelLeft, 'level') === '5') ? true : false;

                    if (isPricingLevel4) { return null; }

                    const pricingMatrixLevelName = (Formatters.getStr(pricingLevelLeft, 'level') === '5') ?
                        `${this.appText('pricing-level')} ${this.appText('pricing-level-renamed-level5')}` :
                        `${this.appText('pricing-level')} ${pricingLevelLeft.level}`;

                    return (
                        <Row key={`order-pricing-matrix-${i}`} className={`order-detail-pricing-matrix-pricing-level-${pricingLevelLeft.level}`}>
                            <Col xs={3} className="entity-detail-values">{pricingMatrixLevelName}</Col>
                            <Col xs={3} className="entity-detail-values"><FormattedNumber value={Formatters.getFloat(pricingLevelLeft, 'economyPrice')} style="currency" currency="USD" /></Col>
                            <Col xs={3} className="entity-detail-values"><FormattedNumber value={Formatters.getFloat(pricingLevelLeft, 'standardPrice')} style="currency" currency="USD" /></Col>
                            <Col xs={3} className="entity-detail-values"><FormattedNumber value={Formatters.getFloat(pricingLevelLeft, 'rushPrice')} style="currency" currency="USD" /></Col>
                        </Row>
                    );
                })
            );

            // Section: 3 Year Plan (Level 4) Pricing
            sections.orderDetailsLeftPricing3YearPlan = _.stubArray();
            if (fdOrderIs3YearPlan) {

                sections.orderDetailsLeftPricing3YearPlan.push(
                    <Row key="fiscal-year-end">
                        <Col xs={3} className="entity-detail-values">{'FYE'}</Col>
                        <Col xs={3} className="entity-detail-values">{Formatters.getStr(order, 'assocFYE')}</Col>
                    </Row>
                );

                sections.orderDetailsLeftPricing3YearPlan.push(
                    <Row key="pricing-matrix-header">
                        <Col xs={3} className="entity-detail-values">{this.appText('order-pricing')}</Col>
                        <Col xs={3} className="entity-detail-label"><strong>{this.appText('pricing-economy')}</strong></Col>
                        <Col xs={3} className="entity-detail-label"><strong>{this.appText('pricing-standard')}</strong></Col>
                        <Col xs={3} className="entity-detail-label"><strong>{this.appText('pricing-rush')}</strong></Col>
                    </Row>
                );

                const fdOspm = _.get(formData, 'orderServicesPricingMatrix', {});
                sections.orderDetailsLeftPricing3YearPlan.push(
                    <Row key={`order-details-left-pricing-3yp-row-pricing`}>
                        <Col xs={3} className="entity-detail-values">{this.appText('pricing-3-year-plan-pricing')}</Col>
                        <Col xs={3} className="entity-detail-values"><FormattedNumber value={Formatters.getFloat(fdOspm, 'economyPricing.price')} style="currency" currency="USD" /></Col>
                        <Col xs={3} className="entity-detail-values"><FormattedNumber value={Formatters.getFloat(fdOspm, 'standardPricing.price')} style="currency" currency="USD" /></Col>
                        <Col xs={3} className="entity-detail-values"><FormattedNumber value={Formatters.getFloat(fdOspm, 'rushPricing.price')} style="currency" currency="USD" /></Col>
                    </Row>
                );

                sections.orderDetailsLeftPricing3YearPlan.push(
                    <Row key={`order-details-left-pricing-3yp-row-year-count`}>
                        <Col xs={3} className="entity-detail-values">{this.appText('pricing-3-year-plan-year-count')}</Col>
                        <Col xs={3} className="entity-detail-values">{Formatters.getInt(formData, 'orderServicesPricingMatrix.planYearSequenceNum')}</Col>
                        <Col xs={6} className="entity-detail-values"><div /></Col>
                    </Row>
                );

                sections.orderDetailsLeftPricing3YearPlan.push(
                    <Row key={`order-details-left-pricing-3yp-row-year`}>
                        <Col xs={3} className="entity-detail-values">{this.appText('pricing-3-year-plan-year')}</Col>
                        <Col xs={3} className="entity-detail-values">{moment(_.get(fdOspm, 'economyPricing.pricingYearDate')).year()}</Col>
                        <Col xs={3} className="entity-detail-values">{moment(_.get(fdOspm, 'standardPricing.pricingYearDate')).year()}</Col>
                        <Col xs={3} className="entity-detail-values">{moment(_.get(fdOspm, 'rushPricing.pricingYearDate')).year()}</Col>
                    </Row>
                );

                sections.orderDetailsLeftPricing3YearPlan.push(
                    <Row key={`order-details-left-pricing-3yp-row-level-due`}>
                        <Col xs={3} className="entity-detail-values">{this.appText('pricing-3-year-plan-level-due')}</Col>
                        <Col xs={3} className="entity-detail-values">{Formatters.getStr(fdOspm, 'economyPricing.levelDueText')}</Col>
                        <Col xs={3} className="entity-detail-values">{Formatters.getStr(fdOspm, 'standardPricing.levelDueText')}</Col>
                        <Col xs={3} className="entity-detail-values">{Formatters.getStr(fdOspm, 'rushPricing.levelDueText')}</Col>
                    </Row>
                );
            }

            // Remaining Sections
            const checked = FormCheckbox.RenderCheckedBox();
            const unchecked = FormCheckbox.RenderUncheckedBox();

            sections.orderDetailsRight.push(new SectionField({ label: (<p />) }));
            sections.orderDetailsRight.push(SectionField.CreateSingle(this.appText('follow-up-date'), Formatters.getStr(order, 'followUpDateFormatted')));
            sections.orderDetailsRight.push(new SectionField({ label: (<p />) }));
            sections.orderDetailsRight.push(SectionField.CreateSingle(this.appText('reminder-bid-date'), Formatters.getStr(order, 'reminderBidDateFormatted')));
            sections.orderDetailsRight.push(new SectionField({ label: (<p />) }));
            sections.orderDetailsRight.push(SectionField.CreateSingle(this.appText('first-reminder-sent'), (Formatters.getStr(order, 'firstReminderSentDateFormatted')) ? Formatters.getStr(order, 'firstReminderSentDateFormatted') : 'Not Yet Sent'));
            sections.orderDetailsRight.push(new SectionField({ label: (<p />) }));
            sections.orderDetailsRight.push(SectionField.CreateSingle(this.appText('second-reminder-sent'), (Formatters.getStr(order, 'secondReminderSentDateFormatted')) ? Formatters.getStr(order, 'secondReminderSentDateFormatted') : 'Not Yet Sent'));
            sections.orderDetailsRight.push(new SectionField({ label: (<p />) }));
            sections.orderDetailsRight.push(SectionField.CreateSingle(this.appText('feedback'), _.get(order, 'feedback', false) ? checked : unchecked));
            sections.orderDetailsRight.push(SectionField.CreateSingle(this.appText('sample-sent'), _.get(order, 'sampleSent', false) ? checked : unchecked));
            sections.orderDetailsRight.push(SectionField.CreateSingle(this.appText('unsolicited-bid'), _.get(order, 'unsolicitedBid', false) ? checked : unchecked));
            sections.orderDetailsRight.push(SectionField.CreateSingle(this.appText('contract-received'), _.get(order, 'contractReceived', false) ? checked : unchecked));
            sections.orderDetailsRight.push(new SectionField({ label: (<p />) }));
            sections.orderDetailsRight.push(SectionField.CreateSingle(this.appText('entered-in-quickbooks'), _.get(order, 'enteredInQuickbooks', false) ? checked : unchecked));
            sections.orderDetailsRight.push(SectionField.CreateSingle(this.appText('invoice-sent-in-quickbooks'), _.get(order, 'invoiceSentInQuickbooks', false) ? checked : unchecked));

            // Rob requested feature 20200114: Display order.inspectorPaid field
            sections.orderDetailsRight.push(SectionField.CreateSingle(this.appText('coordinator-paid'), _.get(order, 'inspectorPaid', false) ? checked : unchecked));
            sections.orderDetailsRight.push(SectionField.CreateSingle(this.appText('worksheet-received'), _.get(order, 'worksheetReceived', false) ? checked : unchecked));
            sections.orderDetailsRight.push(SectionField.CreateSingle(this.appText('use-two-option-pricing'), _.get(order, 'twoOptionPricing', false) ? checked : unchecked));
            sections.orderDetailsRight.push(SectionField.CreateSingle(this.appText('is-preliminary-study'), _.get(order, 'isPreliminaryStudy', false) ? checked : unchecked));
        }

        const page = (
            <div>
                <Dialog ref={(el) => this.attachDialogRef(el)} />

                <div>
                    {this.renderHeaderButtonToolbar(orderId, orders)}
                </div>

                <Row>
                    <Col xs={12} xl={9}>
                        <WhiteBoxPageSection title={this.appText('active-contact-information')}>
                            <Row>
                                <Col xs={6} xl={4}>
                                    <div>
                                        <EntityDetailSection labelColXs={5} valueColXs={7} enabled={sections.enabled} fields={sections.activeContactLeftAddress} title={this.appText('active-contact-association')} />
                                    </div>

                                    <div>
                                        <EntityDetailSection labelColXs={5} valueColXs={7} enabled={sections.enabled} fields={sections.activeContactLeftDetails} />
                                    </div>

                                    {/* <div>
                                        <ButtonToolbar>
                                            <Button type="button" bsStyle="success" onClick={e => { this.handleEditButton(e); }}>{this.appText('edit-order')}</Button>
                                        </ButtonToolbar>
                                    </div> */}
                                </Col>

                                <Col xs={6} xl={4}>
                                    <div>
                                        <EntityDetailSection labelColXs={5} valueColXs={7} enabled={sections.enabled} fields={sections.activeContactRightAssociationClient} title={this.appText('active-contact-client')} />
                                    </div>

                                    <div>
                                        <EntityDetailSection labelColXs={5} valueColXs={7} enabled={sections.enabled} fields={sections.activeContactRightAssociationContact} />
                                    </div>
                                </Col>

                                <Col xs={6} xl={4}>
                                    <div>
                                        <EntityDetailSection labelColXs={5} valueColXs={7} enabled={sections.enabled} fields={sections.activeContactRightOrderClient} title={this.appText('order-client-info')} />
                                    </div>

                                    <div>
                                        <EntityDetailSection labelColXs={5} valueColXs={7} enabled={sections.enabled} fields={sections.activeContactRightOrderContact} />
                                    </div>
                                </Col>
                            </Row>
                        </WhiteBoxPageSection>
                    </Col>
                </Row>

                <Row>
                    <Col xs={7} xl={6}>
                        <WhiteBoxPageSection title={this.appText('order-details')}>
                            <Row>
                                <Col xs={12}>
                                    <div>
                                        <EntityDetailSection labelColXs={3} valueColXs={9} enabled={sections.enabled} fields={sections.orderDetailsLeftInfo} title={this.appText('order-information')} />
                                    </div>

                                    <div>
                                        <EntityDetailSection labelColXs={3} valueColXs={9} enabled={sections.enabled} fields={sections.orderDetailsLeftInfoAssigned} />
                                    </div>

                                    <div>
                                        <EntityDetailSection labelColXs={3} valueColXs={9} enabled={sections.enabled} fields={sections.orderDetailsLeftInfoNotes} />
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col xs={12}>
                                    <EntityDetailSection enabled={sections.enabled} fields={sections.orderDetailsLeftDates} title={this.appText('order-dates')} >
                                        {sections.orderDetailsLeftDates}
                                    </EntityDetailSection>
                                </Col>
                            </Row>

                            <Row>
                                <Col xs={12}>
                                    <EntityDetailSection enabled={sections.enabled} fields={sections.orderDetailsLeftServiceInfo} title={this.appText('order-details')} >
                                        {sections.orderDetailsLeftServiceInfo}
                                    </EntityDetailSection>
                                </Col>
                            </Row>
                        </WhiteBoxPageSection>
                    </Col>

                    <Col xs={5} xl={3}>
                        <WhiteBoxPageSection title={this.appText('order-links')}>
                            <Row>
                                <Col xs={12}>
                                    <div>
                                        {this.renderLinksButtonToolbar()}
                                    </div>
                                    <EntityDetailSection labelColXs={6} valueColXs={6} enabled={sections.enabled} fields={sections.orderDetailsRight} title={''} />
                                </Col>
                            </Row>
                        </WhiteBoxPageSection>
                    </Col>
                </Row>

                <Row>
                    <Col xs={7} xl={6}>
                        <WhiteBoxPageSection title={this.appText('order-pricing')}>
                            <Row>
                                <Col xs={12}>
                                    <Row className="row entity-detail-section">
                                        <Col xs={12}>
                                            {sections.orderDetailsLeftPricing}
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </WhiteBoxPageSection>
                    </Col>
                </Row>

                {(fdOrderIs3YearPlan) ? (
                    <Row>
                        <Col xs={7} xl={6}>
                            <WhiteBoxPageSection title={this.appText('order-pricing-3-year-plan')}>
                                <Row>
                                    <Col xs={12}>
                                        <Row className="row entity-detail-section">
                                            <Col xs={12}>
                                                {sections.orderDetailsLeftPricing3YearPlan}
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </WhiteBoxPageSection>
                        </Col>
                    </Row>) : (<div />)
                }

                <Row>
                    <Col xs={7} xl={6}>
                        <WhiteBoxPageSection title={this.appText('order-follow-up-notes')}>
                            <Row>
                                <Col xs={12}>
                                    <Row className="row entity-detail-section">
                                        <Col xs={12}>
                                            {this.renderOrderFollowUpNotes(formData)}
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </WhiteBoxPageSection>
                    </Col>
                </Row>

                <div>
                    {this.renderHeaderButtonToolbar(orderId, orders)}
                </div>
            </div>
        );

        const pageTitle = sections.title;
        const { ...rest } = this.props;

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrl = props => ApiEndpoints.getOrderDetailUrl(props.match.params.orderId);

const mapPropsToConfigs = (props) => ReduxQueries.Queries.getOrderDetailRequest(
    getApiUrl(props),
    ReduxQueries.PropsAccessToken(props),
);

const mapStateToProps = (state, props) => {
    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(props), state, props, {
        orderId: props.match.params.orderId,
        orderDetail: state.entities.orderDetailOrder,
        orderChartDetail: state.entities.orderDetailOrderChart,
        orders: state.entities.orderDetailOrders,
        association: state.entities.orderDetailAssociation,
    });
};

const mapDispatchToProps = dispatch => ({
    // newOrder: MutateActions.newOrderMutateAsync,
    newOrder: (d: any, t: any, o: any) => dispatch(MutateActions.newOrderMutateAsync(d, t, o)),
    navigateTo: (path) => dispatch(push(path)),
    updateReminderBidsSendReminderEmail: (d: any, a: string, o: boolean) => dispatch(MutateActions.updateReminderBidsSendReminderEmailMutateAsync(d, a, o)),
});

const OrderContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(Order);

export default withRouter(OrderContainer);



// WEBPACK FOOTER //
// ./src/order/index.tsx